// src/components/CompanySelectPage.js
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, CssBaseline, Typography, Stack, Card, Avatar, Chip, CircularProgress, Alert,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import BusinessIcon from '@mui/icons-material/Business';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

import ThemeToggleButton from './ThemeToggleButton';
import BracketLogo from './AppShell/BracketLogo';
import { ConfigContext } from './shared/ConfigContext';
import { applyCompanyContext } from './AppShell/AppShell';
import { performLogout } from '../utils/logout';
import useMemberships from '../hooks/useMemberships';
import { radius, layout } from '../themes/smartinventoryTheme';

const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
};

// Shown right after login when the caller belongs to more than one company
// (see persistGoogleUser/persistMicrosoftUser) — a full-screen "choose a
// workspace" step, on the model of Slack/Google's account picker. The caller
// is already validly signed into a default company by the time they land
// here (see resolve_login_context on the backend), so this only needs to
// confirm/switch, never to complete authentication itself.
const CompanySelectPage = ({ toggleTheme }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const config = useContext(ConfigContext);
  const { memberships, loading, error, fetchMemberships } = useMemberships();
  const [selectingId, setSelectingId] = useState(null);
  const [switchError, setSwitchError] = useState('');

  useEffect(() => {
    if (config?.backendUrl) fetchMemberships();
  }, [config, fetchMemberships]);

  // Nothing to choose between (direct navigation here, or the caller's
  // membership set shrank to one since login) — just continue in.
  useEffect(() => {
    if (memberships && memberships.length <= 1) {
      window.location.href = '/';
    }
  }, [memberships]);

  if (!config || !config.backendUrl) {
    return null;
  }

  const handleSelect = async (companyId) => {
    if (selectingId) return;
    setSelectingId(companyId);
    setSwitchError('');
    try {
      const res = await axios.post(`${config.backendUrl}/auth/switch-company`, { companyId });
      window.location.href = applyCompanyContext(res.data);
    } catch (err) {
      console.error('Error switching company', err);
      setSwitchError(err.response?.data?.error || t('Could not switch companies.', { defaultValue: 'Could not switch companies.' }));
      setSelectingId(null);
    }
  };

  const showPicker = !loading && !error && memberships && memberships.length > 1;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: 'background.default' }}>
      <CssBaseline />

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: { xs: 3, sm: 6 }, py: 5 }}>
        <BracketLogo />
        <ThemeToggleButton toggleTheme={toggleTheme} />
      </Box>

      <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', px: 3 }}>
        <Box sx={{ width: '100%', maxWidth: 460, mt: { xs: 1, sm: 3 } }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Box
              sx={{
                width: layout.stateIconSize, height: layout.stateIconSize, borderRadius: '50%',
                bgcolor: 'primary.light', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', mb: 2,
              }}
            >
              <BusinessIcon color="primary" />
            </Box>
            <Typography variant="h4" fontWeight={600} sx={{ lineHeight: 1.2 }}>
              {t('Choose a workspace', { defaultValue: 'Choose a workspace' })}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {t('You have access to more than one company. Select one to continue.', { defaultValue: 'You have access to more than one company. Select one to continue.' })}
            </Typography>
          </Box>

          {loading && (
            <Stack spacing={1.25}>
              {[0, 1, 2].map((i) => (
                <Box key={i} sx={{ height: 72, borderRadius: `${radius.xl}px`, bgcolor: 'action.hover' }} />
              ))}
            </Stack>
          )}

          {!loading && error && (
            <Alert severity="error" sx={{ mb: 1 }}>
              {t('Could not load your companies.', { defaultValue: 'Could not load your companies.' })}{' '}
              <Typography
                component="button"
                onClick={fetchMemberships}
                variant="body2"
                fontWeight={600}
                sx={{ color: 'inherit', textDecoration: 'underline', cursor: 'pointer', bgcolor: 'transparent', border: 'none', font: 'inherit', p: 0 }}
              >
                {t('Retry', { defaultValue: 'Retry' })}
              </Typography>
            </Alert>
          )}

          {switchError && (
            <Alert severity="error" sx={{ mb: 1.5 }}>{switchError}</Alert>
          )}

          {showPicker && (
            <Stack spacing={1.25}>
              {memberships.map((m) => {
                const isSelecting = selectingId === m.companyId;
                const disabled = Boolean(selectingId) && !isSelecting;
                return (
                  <Card
                    key={m.companyId}
                    onClick={() => !selectingId && handleSelect(m.companyId)}
                    role="button"
                    tabIndex={disabled ? -1 : 0}
                    aria-label={m.companyName || t('Company', { defaultValue: 'Company' })}
                    onKeyDown={(e) => {
                      if ((e.key === 'Enter' || e.key === ' ') && !selectingId) {
                        e.preventDefault();
                        handleSelect(m.companyId);
                      }
                    }}
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 1.5, p: 1.75,
                      cursor: disabled ? 'default' : 'pointer',
                      opacity: disabled ? 0.5 : 1,
                      transition: 'border-color 0.15s ease, transform 0.15s ease, box-shadow 0.15s ease',
                      '&:hover': disabled ? {} : {
                        borderColor: 'primary.main',
                        boxShadow: (theme) => `0 0 0 1px ${alpha(theme.palette.primary.main, 0.25)}`,
                      },
                    }}
                  >
                    <Avatar sx={{ width: 40, height: 40, fontSize: 14 }}>{getInitials(m.companyName)}</Avatar>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="subtitle1" noWrap>
                        {m.companyName || t('Untitled company', { defaultValue: 'Untitled company' })}
                      </Typography>
                      <Chip
                        label={m.role === 'admin' ? t('Admin') : t('User', { defaultValue: 'User' })}
                        size="small"
                        sx={{ mt: 0.5 }}
                      />
                    </Box>
                    {isSelecting ? (
                      <CircularProgress size={18} />
                    ) : (
                      <ChevronRightIcon sx={{ color: 'text.secondary', flexShrink: 0 }} />
                    )}
                  </Card>
                );
              })}
            </Stack>
          )}

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ textAlign: 'center', mt: 4, display: 'block' }}
          >
            {t('Not you?', { defaultValue: 'Not you?' })}{' '}
            <Typography
              component="span"
              variant="caption"
              fontWeight={600}
              onClick={() => performLogout(navigate)}
              sx={{ color: 'primary.main', cursor: 'pointer' }}
            >
              {t('Sign out', { defaultValue: 'Sign out' })}
            </Typography>
          </Typography>
        </Box>
      </Box>

      <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', pb: 3 }}>
        © 2026 SmartInventory
      </Typography>
    </Box>
  );
};

export default CompanySelectPage;
